import type {
  RawComponent,
  RawQuestion,
  RawQuestionSection,
  RawQuestionSettings,
  RawSection,
  RawSectionStats,
} from '@/features/content/useComponent';

export interface ScoreSummary {
  homeworkMaxScore: number;
  homeworkPercentage: number;
  homeworkStudentScore: number;
  preLectureMaxScore: number;
  preLecturePercentage: number;
  preLectureStudentScore: number;
}

type QuestionAssignmentType = 'ALL' | 'FIXED' | 'RANDOM' | 'USER_DEFINED';

const QUESTION_ASSIGNMENT_TYPE_MAP: Record<string, QuestionAssignmentType> = {
  '0': 'ALL',
  '1': 'ALL',
  '2': 'FIXED',
  '3': 'RANDOM',
  '4': 'USER_DEFINED',
  ALL: 'ALL',
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  UNKNOWN: 'ALL',
  USER_DEFINED: 'USER_DEFINED',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toQuestionArray(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

function toSectionStatsArray(value: unknown): RawSectionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSectionStats[];
}

function toIntegerArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeNumber(entry))
    .filter((entry): entry is number => entry !== undefined)
    .map((entry) => Math.trunc(entry))
    .filter((entry) => entry >= 0);
}

function getQuestionSection(
  section: RawSection,
  kind: 'homework' | 'preLecture',
): RawQuestionSection | null {
  const candidate =
    kind === 'preLecture'
      ? section.pre_lecture_question_section ?? section.preLectureQuestionSection
      : section.question_section ?? section.questionSection;

  return isRecord(candidate) ? (candidate as RawQuestionSection) : null;
}

function getQuestionSettings(
  component: RawComponent,
  kind: 'homework' | 'preLecture',
): RawQuestionSettings | null {
  const candidate =
    kind === 'preLecture'
      ? component.pre_lecture_question_settings ?? component.preLectureQuestionSettings
      : component.question_settings ??
        component.questionSettings ??
        component.course_question_settings ??
        component.courseQuestionSettings;

  return isRecord(candidate) ? (candidate as RawQuestionSettings) : null;
}

function normalizeAssignmentType(value: unknown): QuestionAssignmentType {
  if (typeof value === 'number') {
    return QUESTION_ASSIGNMENT_TYPE_MAP[String(value)] ?? 'ALL';
  }

  const normalized = normalizeString(value);
  return normalized
    ? QUESTION_ASSIGNMENT_TYPE_MAP[normalized.toUpperCase()] ?? 'ALL'
    : 'ALL';
}

function getAssignedQuestionIndexes(
  questions: RawQuestion[],
  settings: RawQuestionSettings | null,
) {
  if (normalizeAssignmentType(settings?.type) !== 'USER_DEFINED') {
    return new Set(questions.map((_, index) => index));
  }

  return new Set(
    toIntegerArray(
      settings?.user_defined_numbers ?? settings?.userDefinedNumbers,
    ),
  );
}

export function getQuestionMaxScore(question: RawQuestion): number {
  const questionType =
    normalizeString(question.type)?.toUpperCase() ??
    (typeof question.type === 'number' ? String(question.type) : null);

  if (questionType === 'MULTI_PART' || questionType === '5') {
    const rawParts = question.question_set?.questions ?? question.questionSet?.questions;
    const parts = toQuestionArray(rawParts);

    if (parts.length > 0) {
      return parts.reduce((total, part) => total + getQuestionMaxScore(part), 0);
    }
  }

  const explicitValue = normalizeNumber(question.value);
  return explicitValue !== undefined && explicitValue > 0 ? explicitValue : 1;
}

function getSectionMaxScore(
  section: RawSection,
  settings: RawQuestionSettings | null,
  kind: 'homework' | 'preLecture',
) {
  const questionSection = getQuestionSection(section, kind);

  if (!questionSection) {
    return 0;
  }

  const questions = toQuestionArray(questionSection.questions);
  const assignedQuestionIndexes = getAssignedQuestionIndexes(questions, settings);

  return questions.reduce((total, question, index) => {
    // A removed question stays in the list, because the settings name positions.
    if (!assignedQuestionIndexes.has(index) || question.removed === true) {
      return total;
    }

    return total + getQuestionMaxScore(question);
  }, 0);
}

function getStudentScore(
  sectionStats: RawSectionStats,
  kind: 'homework' | 'preLecture',
) {
  return normalizeNumber(
    kind === 'preLecture'
      ? sectionStats.total_pre_lecture_score ?? sectionStats.totalPreLectureScore
      : sectionStats.total_score ?? sectionStats.totalScore,
  ) ?? 0;
}

function getPercentage(score: number, maxScore: number) {
  if (maxScore <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (score / maxScore) * 100));
}

export function getScoreSummary(component: RawComponent): ScoreSummary {
  const sections = toSectionArray(component.sections);
  const sectionStats = toSectionStatsArray(
    component.section_stats ?? component.sectionStats,
  );
  const homeworkSettings = getQuestionSettings(component, 'homework');
  const preLectureSettings = getQuestionSettings(component, 'preLecture');

  const homeworkMaxScore = sections.reduce(
    (total, section) => total + getSectionMaxScore(section, homeworkSettings, 'homework'),
    0,
  );
  const preLectureMaxScore = sections.reduce(
    (total, section) => total + getSectionMaxScore(section, preLectureSettings, 'preLecture'),
    0,
  );
  const homeworkStudentScore = sectionStats.reduce(
    (total, stats) => total + getStudentScore(stats, 'homework'),
    0,
  );
  const preLectureStudentScore = sectionStats.reduce(
    (total, stats) => total + getStudentScore(stats, 'preLecture'),
    0,
  );

  return {
    homeworkMaxScore,
    homeworkPercentage: getPercentage(homeworkStudentScore, homeworkMaxScore),
    homeworkStudentScore,
    preLectureMaxScore,
    preLecturePercentage: getPercentage(preLectureStudentScore, preLectureMaxScore),
    preLectureStudentScore,
  };
}

